import React, { useCallback } from 'react';
import { Page, LayoutSingleColumn, NamedLink } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import MobileBottomNavbar from '../../components/MobileBottomNavbar/MobileBottomNavbar';
import { Button } from '../../components';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import css from './coachRentalPage.module.css';

import AliciaImage from '../../assets/alisa-avatar.png';
import GabrielImage from '../../assets/gabriel-avatar.png';
import SophieImage from '../../assets/sophie-avatar.png';

const coachRentalPageComponent = () => {
  const intl = useIntl();
  const pageTitle = intl.formatMessage({
    id: 'CoachRentalPage.pageTitle',
    defaultMessage: 'Free 1-on-1 Help to Grow Your Rental Business',
  });

  const calendlyUrl = process.env.REACT_APP_CALENDLY_URL || 'https://calendly.com/hey-rentanythingstore/30min';
  const coachAvatar = 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face'
  const handleCalendlyRedirect = useCallback(() => {
    if (typeof window !== 'undefined' && calendlyUrl) {
      window.open(calendlyUrl, '_blank', 'noopener,noreferrer');
    }
  }, [calendlyUrl]);

  const handleScrollToBooking = useCallback(() => {
    const bookingSection = document.querySelector('[data-section="booking"]');
    if (bookingSection) {
      bookingSection.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  return (
    <Page title={pageTitle}>
      <LayoutSingleColumn
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
        mobileBottomNavbar={<MobileBottomNavbar />}
      >
        <div className={css.root}>
          {/* Hero Section */}
          <section className={css.heroSection}>
            <div className={css.heroContainer}>
              <div className={css.heroContent}>
                <div className={css.heroBadge}>
                  <FormattedMessage
                    id="CoachRentalPage.heroBadge"
                    defaultMessage="100% Free • No Credit Card Required"
                  />
                </div>
                <h1 className={css.heroTitle}>
                  <FormattedMessage
                    id="CoachRentalPage.heroTitlePrefix"
                    defaultMessage="Free 1-on-1 Help to"
                  />{' '}
                  <span className={css.heroTitleHighlight}>
                    <FormattedMessage
                      id="CoachRentalPage.heroTitleHighlight"
                      defaultMessage="Grow Your Rental Business"
                    />
                  </span>
                </h1>
                <p className={css.heroDescription}>
                  <FormattedMessage
                    id="CoachRentalPage.heroDescription"
                    defaultMessage="Our Rental Coaches can help you list smarter, earn faster, and stand out in the marketplace, all at no cost."
                  />
                </p>
                <div className={css.heroButtons}>
                <Button className={`${css.primaryButton} ${css.customButton}`}
                    icon={<i className="ri-calendar-line" />}
                    arrow={"left"}
                    onClick={handleScrollToBooking}
                  >
                    <span>
                      <FormattedMessage
                        id="CoachRentalPage.heroPrimaryCta"
                        defaultMessage="Book Free Session"
                      />
                    </span>
                  </Button>
                  <NamedLink
                    variant="secondary-outline"
                    size="large"
                    className={css.secondaryButton}
                    name="CMSPage"
                    params={{
                      pageId: 'faq',
                    }}


                  >
                    <FormattedMessage
                      id="CoachRentalPage.heroSecondaryCta"
                      defaultMessage="Learn How It Works"
                    />
                    <i className="ri-arrow-right-line" />
                  </NamedLink>
                </div>
              </div>
              <div className={css.heroImage}>
                <div className={css.imagePlaceholder}>
                  <img
                    src="https://res.cloudinary.com/dja5jmxiz/image/upload/w_900,f_auto,q_auto:good/v1780486445/CB07572A-4A21-4D93-9D5A-54A1517F6162_zazdxn.png"
                    srcSet="https://res.cloudinary.com/dja5jmxiz/image/upload/w_480,f_auto,q_auto:good/v1780486445/CB07572A-4A21-4D93-9D5A-54A1517F6162_zazdxn.png 480w, https://res.cloudinary.com/dja5jmxiz/image/upload/w_768,f_auto,q_auto:good/v1780486445/CB07572A-4A21-4D93-9D5A-54A1517F6162_zazdxn.png 768w, https://res.cloudinary.com/dja5jmxiz/image/upload/w_900,f_auto,q_auto:good/v1780486445/CB07572A-4A21-4D93-9D5A-54A1517F6162_zazdxn.png 900w, https://res.cloudinary.com/dja5jmxiz/image/upload/w_1200,f_auto,q_auto:good/v1780486445/CB07572A-4A21-4D93-9D5A-54A1517F6162_zazdxn.png 1200w"
                    sizes="(max-width: 767px) 100vw, 50vw"
                    alt="Rental coach session"
                    className={css.coachImage}
                    loading="eager"
                    fetchpriority="high"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Who Is This For Section */}
          <section className={css.whoSection}>
            <div className={css.sectionContainer}>
              <h2 className={css.sectionTitle}>
                <FormattedMessage
                  id="CoachRentalPage.whoTitle"
                  defaultMessage="Who Is This For?"
                />
              </h2>
              <p className={css.sectionSubtitle}>
                <FormattedMessage
                  id="CoachRentalPage.whoSubtitle"
                  defaultMessage="Whether you're just starting out or scaling up, our coaches are here to help you succeed."
                />
              </p>
              <div className={css.cardsGrid}>
                <div className={css.card}>
                  <div className={css.cardIcon}>
                    <i className="ri-parent-line" />
                  </div>
                  <h3 className={css.cardTitle}>
                    <FormattedMessage
                      id="CoachRentalPage.whoNewRentersTitle"
                      defaultMessage="New Renters"
                    />
                  </h3>
                  <p className={css.cardDescription}>
                    <FormattedMessage
                      id="CoachRentalPage.whoNewRentersDescription"
                      defaultMessage="Get help with listing your first items, setting prices, and taking great photos."
                    />
                  </p>
                </div>
                <div className={css.card}>
                  <div className={css.cardIcon}>
                    <i className="ri-line-chart-line" />
                  </div>
                  <h3 className={css.cardTitle}>
                    <FormattedMessage
                      id="CoachRentalPage.whoSideHustlersTitle"
                      defaultMessage="Side Hustlers"
                    />
                  </h3>
                  <p className={css.cardDescription}>
                    <FormattedMessage
                      id="CoachRentalPage.whoSideHustlersDescription"
                      defaultMessage="Learn how to find your niche, source high-demand items, and attract repeat renters."
                    />
                  </p>
                </div>
                <div className={css.card}>
                  <div className={css.cardIcon}>
                    <i className="ri-store-3-line" />
                  </div>
                  <h3 className={css.cardTitle}>
                    <FormattedMessage
                      id="CoachRentalPage.whoBusinessesTitle"
                      defaultMessage="Established Businesses"
                    />
                  </h3>
                  <p className={css.cardDescription}>
                    <FormattedMessage
                      id="CoachRentalPage.whoBusinessesDescription"
                      defaultMessage="Get advanced support for scaling, integrations, and custom store strategies."
                    />
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* What You'll Learn Section */}
          <section className={css.learnSection}>
            <div className={css.sectionContainer}>
              <div className={css.learnContent}>
                <div className={css.learnLeft}>
                  <div className={css.learnBadge}>
                    <i className="ri-book-open-line" />
                    <span>
                      <FormattedMessage
                        id="CoachRentalPage.learnBadge"
                        defaultMessage="Personalized Coaching"
                      />
                    </span>
                  </div>
                  <h2 className={css.sectionTitle}>
                    <FormattedMessage
                      id="CoachRentalPage.learnTitle"
                      defaultMessage="What You'll Learn"
                    />
                  </h2>
                  <p className={css.sectionSubtitle}>
                    <FormattedMessage
                      id="CoachRentalPage.learnSubtitle"
                      defaultMessage="Every session is tailored to your unique needs and goals. Here's what our coaches can help you with:"
                    />
                  </p>
                </div>
                <div className={css.learnRight}>
                  <div className={css.learnList}>
                    <div className={css.learnItem}>
                      <div className={css.learnItemIcon}>
                        <i className="ri-focus-2-line" />
                      </div>
                      <p className={css.learnItemText}>
                        <FormattedMessage
                          id="CoachRentalPage.learnItemChoose"
                          defaultMessage="Choose profitable items that renters actually want"
                        />
                      </p>
                    </div>
                    <div className={css.learnItem}>
                      <div className={css.learnItemIcon}>
                        <i className="ri-money-dollar-circle-line" />
                      </div>
                      <p className={css.learnItemText}>
                        <FormattedMessage
                          id="CoachRentalPage.learnItemPricing"
                          defaultMessage="Set competitive prices that maximize your earnings"
                        />
                      </p>
                    </div>
                    <div className={css.learnItem}>
                      <div className={css.learnItemIcon}>
                        <i className="ri-shield-check-line" />
                      </div>
                      <p className={css.learnItemText}>
                        <FormattedMessage
                          id="CoachRentalPage.learnItemProtect"
                          defaultMessage="Protect your inventory and manage risk effectively"
                        />
                      </p>
                    </div>
                    <div className={css.learnItem}>
                      <div className={css.learnItemIcon}>
                        <i className="ri-star-smile-line" />
                      </div>
                      <p className={css.learnItemText}>
                        <FormattedMessage
                          id="CoachRentalPage.learnItemReviews"
                          defaultMessage="Handle customer inquiries and build 5-star reviews"
                        />
                      </p>
                    </div>
                    <div className={css.learnItem}>
                      <div className={css.learnItemIcon}>
                        <i className="ri-line-chart-line" />
                      </div>
                      <p className={css.learnItemText}>
                        <FormattedMessage
                          id="CoachRentalPage.learnItemScale"
                          defaultMessage="Scale your business and create recurring revenue"
                        />
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Testimonials Section */}
          <section className={css.testimonialsSection}>
            <div className={css.sectionContainer}>
              <h2 className={css.sectionTitle}>
                <FormattedMessage
                  id="CoachRentalPage.testimonialsTitle"
                  defaultMessage="What Renters Are Saying"
                />
              </h2>
              <p className={css.sectionSubtitle}>
                <FormattedMessage
                  id="CoachRentalPage.testimonialsSubtitle"
                  defaultMessage="Real success stories from renters who've grown their businesses with our coaching."
                />
              </p>
              <div className={css.testimonialsGrid}>
                <div className={css.testimonialCard}>
                  <div className={css.stars}>
                    {[...Array(5)].map((_, i) => (
                      <i key={`testimonial-star-1-${i}`} className="ri-star-fill" />
                    ))}
                  </div>
                  <p className={css.testimonialText}>
                    <FormattedMessage
                      id="CoachRentalPage.testimonialOneText"
                      defaultMessage="&quot;The coaching session helped me triple my monthly rental income in just 3 months. The pricing strategy alone was worth its weight in gold!&quot;"
                    />
                  </p>
                  <div className={css.testimonialAuthor}>
                    <div className={css.authorAvatar}>
                      <img
                        src={AliciaImage}
                        alt="Alicia F."
                      />
                    </div>
                    <div className={css.authorInfo}>
                      <div className={css.authorName}>
                        <FormattedMessage
                          id="CoachRentalPage.testimonialOneName"
                          defaultMessage="Alicia F."
                        />
                      </div>
                      <div className={css.authorRole}>
                        <FormattedMessage
                          id="CoachRentalPage.testimonialOneRole"
                          defaultMessage="Baby Gears Renter - Ottawa, ON"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className={css.testimonialCard}>
                  <div className={css.stars}>
                    {[...Array(5)].map((_, i) => (
                      <i key={`testimonial-star-2-${i}`} className="ri-star-fill" />
                    ))}
                  </div>
                  <p className={css.testimonialText}>
                    <FormattedMessage
                      id="CoachRentalPage.testimonialTwoText"
                      defaultMessage="&quot;I was nervous about renting out my tools, but my coach showed me exactly how to protect my investment. Now I'm earning additional income with zero issues.&quot;"
                    />
                  </p>
                  <div className={css.testimonialAuthor}>
                    <div className={css.authorAvatar}>
                      <img
                        src={GabrielImage}
                        alt="Gabriel M."
                      />
                    </div>
                    <div className={css.authorInfo}>
                      <div className={css.authorName}>
                        <FormattedMessage
                          id="CoachRentalPage.testimonialTwoName"
                          defaultMessage="Gabriel M."
                        />
                      </div>
                      <div className={css.authorRole}>
                        <FormattedMessage
                          id="CoachRentalPage.testimonialTwoRole"
                          defaultMessage="Equipment Renter - Florida, USA"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className={css.testimonialCard}>
                  <div className={css.stars}>
                    {[...Array(5)].map((_, i) => (
                      <i key={`testimonial-star-3-${i}`} className="ri-star-fill" />
                    ))}
                  </div>
                  <p className={css.testimonialText}>
                    <FormattedMessage
                      id="CoachRentalPage.testimonialThreeText"
                      defaultMessage="&quot;As a complete beginner, I didn't know where to start. My coach guided me step-by-step, and now I'm getting ready to create my first rental store.&quot;"
                    />
                  </p>
                  <div className={css.testimonialAuthor}>
                    <div className={css.authorAvatar}>
                      <img
                        src={SophieImage}
                        alt="Sophie G."
                      />
                    </div>
                    <div className={css.authorInfo}>
                      <div className={css.authorName}>
                        <FormattedMessage
                          id="CoachRentalPage.testimonialThreeName"
                          defaultMessage="Sophie G."
                        />
                      </div>
                      <div className={css.authorRole}>
                        <FormattedMessage
                          id="CoachRentalPage.testimonialThreeRole"
                          defaultMessage="Freelance Photographer - London, UK"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* How It Works Section */}
          <section className={css.howItWorksSection}>
            <div className={css.sectionContainer}>
              <h2 className={css.sectionTitle}>
                <FormattedMessage
                  id="CoachRentalPage.howTitle"
                  defaultMessage="How It Works"
                />
              </h2>
              <p className={css.sectionSubtitle}>
                <FormattedMessage
                  id="CoachRentalPage.howSubtitle"
                  defaultMessage="Getting started is easy."
                />
              </p>
              <div className={css.stepsContainer}>
                <div className={css.step}>
                  <div className={css.stepNumber}>1</div>
                  <div className={css.stepCard}>
                    <h3 className={css.stepTitle}>
                      <FormattedMessage
                        id="CoachRentalPage.stepOneTitle"
                        defaultMessage="Book your free session"
                      />
                    </h3>
                    <p className={css.stepDescription}>
                      <FormattedMessage
                        id="CoachRentalPage.stepOneDescription"
                        defaultMessage="Pick a time that fits your schedule, no commitment required."
                      />
                    </p>
                  </div>
                </div>
                <div className={css.stepConnector} />
                <div className={css.step}>
                  <div className={css.stepNumber}>2</div>
                  <div className={css.stepCard}>
                    <h3 className={css.stepTitle}>
                      <FormattedMessage
                        id="CoachRentalPage.stepTwoTitle"
                        defaultMessage="Meet your coach"
                      />
                    </h3>
                    <p className={css.stepDescription}>
                      <FormattedMessage
                        id="CoachRentalPage.stepTwoDescription"
                        defaultMessage="Join a 1-on-1 call to discuss your listings, goals, and challenges."
                      />
                    </p>
                  </div>
                </div>
                <div className={css.stepConnector} />
                <div className={css.step}>
                  <div className={css.stepNumber}>3</div>
                  <div className={css.stepCard}>
                    <h3 className={css.stepTitle}>
                      <FormattedMessage
                        id="CoachRentalPage.stepThreeTitle"
                        defaultMessage="Apply expert tips"
                      />
                    </h3>
                    <p className={css.stepDescription}>
                      <FormattedMessage
                        id="CoachRentalPage.stepThreeDescription"
                        defaultMessage="Use personalized insights to improve your listings and boost your earnings."
                      />
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Booking Section */}
          <section className={css.bookingSection} data-section="booking">
            <div className={css.sectionContainer}>
              <h2 className={css.bookingTitle}>
                <FormattedMessage
                  id="CoachRentalPage.bookingTitle"
                  defaultMessage="Book Your Free Coaching Session"
                />
              </h2>
              <p className={css.bookingSubtitle}>
                <FormattedMessage
                  id="CoachRentalPage.bookingSubtitle"
                  defaultMessage="Choose a time that works best for you. Your coach will be ready to help you succeed."
                />
              </p>
              <div className={css.bookingCard}>
                {/* <div className={css.bookingLeftPanel}>
                  <div className={css.coachProfile}>
                    <div className={css.coachAvatar}>
                      <img
                        src={coachAvatar}
                        alt={intl.formatMessage({
                          id: 'CoachRentalPage.coachName',
                          defaultMessage: 'Steve Cody',
                        })}
                      />
                    </div>
                    <div className={css.coachName}>
                      <FormattedMessage
                        id="CoachRentalPage.coachName"
                        defaultMessage="Steve Cody"
                      />
                    </div>
                    <div className={css.meetingTitle}>
                      <FormattedMessage
                        id="CoachRentalPage.meetingTitle"
                        defaultMessage="30 Minute Meeting"
                      />
                    </div>
                    <div className={css.meetingDetails}>
                      <div className={css.detailItem}>
                        <i className="ri-time-line" />
                        <span>
                          <FormattedMessage
                            id="CoachRentalPage.meetingDuration"
                            defaultMessage="30 min"
                          />
                        </span>
                      </div>
                      <div className={css.detailItem}>
                        <i className="ri-video-line" />
                        <span>
                          <FormattedMessage
                            id="CoachRentalPage.meetingDetailVideo"
                            defaultMessage="Web conferencing details provided upon confirmation."
                          />
                        </span>
                      </div>
                    </div>
                    <div className={css.cookieLink}>
                      <a href="#cookie-settings">
                        <FormattedMessage
                          id="CoachRentalPage.cookieSettings"
                          defaultMessage="Cookie settings"
                        />
                      </a>
                    </div>
                  </div>
                </div> */}
                <div className={css.bookingRightPanel}>
                  {/* <div className={css.calendlyBadge}>
                    <span>
                      <FormattedMessage
                        id="CoachRentalPage.calendlyBadge"
                        defaultMessage="POWERED BY Calendly"
                      />
                    </span>
                  </div>
       */}

                  {/* Calendly inline embed – replace the data-url with your own scheduling link */}
                  <div
                    className={css.calendlyEmbedWrapper}
                    data-url={calendlyUrl}
                  >
                    <iframe
                      title={intl.formatMessage({ id: 'CoachRentalPage.bookCoachingSessionTitle' })}
                      src={calendlyUrl}
                      className={css.calendlyIframe}
                      frameBorder="0"
                      allow="camera; microphone; autoplay; encrypted-media"
                      bgcolor="#FFFFFF"
                    />
                  </div>

                </div>
              </div>
            </div>
          </section>

          {/* Orange Footer CTA Section */}
          <section className={css.ctaFooterSection}>
            <div className={css.ctaFooterContainer}>
              <h2 className={css.ctaFooterTitle}>
                <FormattedMessage
                  id="CoachRentalPage.ctaTitle"
                  defaultMessage="Ready to Take Your Rental Business to the Next Level?"
                />
              </h2>
              <p className={css.ctaFooterDescription}>
                <FormattedMessage
                  id="CoachRentalPage.ctaDescription"
                  defaultMessage="Join hundreds of successful renters who've transformed their businesses with free personalized coaching."
                />
              </p>
              <Button
                variant="primary"
                size="large"
                className={css.ctaFooterButton}
                arrow="right"
                icon={<i className="ri-arrow-right-line" />}
                onClick={handleScrollToBooking}
              >
                <FormattedMessage
                  id="CoachRentalPage.ctaButton"
                  defaultMessage="Book Your Free Session Now"
                />
              </Button>
              <div className={css.ctaFooterBenefits}>
                <div className={css.benefitItem}>
                  {/* <div className={css.strikethroughIcon}> */}
                    <i className="ri-no-credit-card-line" />
                  {/* </div> */}
                  <span>
                    <FormattedMessage
                      id="CoachRentalPage.ctaBenefitOne"
                      defaultMessage="No credit card required"
                    />
                  </span>
                </div>
                <div className={css.benefitItem}>
                  <i className="ri-magic-line" />
                  <span>
                    <FormattedMessage
                      id="CoachRentalPage.ctaBenefitTwo"
                      defaultMessage="100% free forever"
                    />
                  </span>
                </div>
                <div className={css.benefitItem}>
                  <i className="ri-shield-check-line" />
                  <span>
                    <FormattedMessage
                      id="CoachRentalPage.ctaBenefitThree"
                      defaultMessage="No commitment"
                    />
                  </span>
                </div>
              </div>
            </div>
          </section>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default coachRentalPageComponent;
